import { Check, Funnel } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Filtro tipo Excel para una columna de tabla: dropdown con búsqueda +
 * checkboxes de los valores distintos presentes en el dataset actual.
 *
 * Convención de `seleccion` (igual que FiltroCategorias):
 * - `null` = TODOS los valores incluidos (sin filtro).
 * - `Set`  = exactamente los valores incluidos (puede estar vacío = ninguno).
 */
interface FiltroColumnaValoresProps {
  etiqueta: string;
  valores: string[];
  seleccion: Set<string> | null;
  onChange: (seleccion: Set<string> | null) => void;
}

export function FiltroColumnaValores({
  etiqueta,
  valores,
  seleccion,
  onChange,
}: FiltroColumnaValoresProps) {
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  const incluido = (v: string) => seleccion === null || seleccion.has(v);
  const incluidasCount = seleccion === null ? valores.length : valores.filter(incluido).length;
  const hayFiltro = seleccion !== null && incluidasCount !== valores.length;

  const toggle = (v: string) => {
    const base = seleccion ?? new Set(valores);
    const next = new Set(base);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next.size === valores.length ? null : next);
  };

  const valoresFiltrados = useMemo(
    () => (busca ? valores.filter((v) => v.toLowerCase().includes(busca.toLowerCase())) : valores),
    [valores, busca],
  );

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAbierto((v) => !v);
        }}
        className={`ml-1 rounded p-0.5 ${hayFiltro ? 'text-blue-600' : 'text-zinc-400 hover:text-zinc-600'}`}
        title={`Filtrar por ${etiqueta}`}
        aria-label={`Filtrar por ${etiqueta}`}
      >
        <Funnel className="h-3 w-3" weight={hayFiltro ? 'fill' : 'bold'} />
      </button>

      {abierto && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: solo detiene la propagación del click, no es interactivo en sí
        <div
          className="absolute left-0 z-50 mt-1 w-64 rounded-md border border-zinc-200 bg-white text-left font-normal shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-zinc-100 px-2 py-1.5">
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={`Buscar ${etiqueta.toLowerCase()}…`}
              className="h-6 w-full rounded border border-zinc-300 px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-1.5 text-xs">
            <span className="font-medium text-muted-foreground">
              {incluidasCount}/{valores.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-blue-600 hover:underline"
              >
                Todas
              </button>
              <span className="text-zinc-300">|</span>
              <button
                type="button"
                onClick={() => onChange(new Set())}
                className="text-blue-600 hover:underline"
              >
                Ninguna
              </button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {valoresFiltrados.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">Sin coincidencias.</p>
            ) : (
              valoresFiltrados.map((v) => {
                const checked = incluido(v);
                return (
                  <button
                    type="button"
                    key={v}
                    onClick={() => toggle(v)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-zinc-50"
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        checked
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-zinc-300 bg-white'
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" weight="bold" />}
                    </span>
                    <span className="flex-1 truncate" title={v}>
                      {v}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
